import { CostPriceAnalysis } from '../../../services/costPrice/costPriceService';
import { COST_PRICE_TYPE_CHOICE } from '../../../utils/costPriceUtils';
import { PerDishUsage } from '../../../utils/reports/inventoryVariance';
import { winningDishAnalyses } from '../../../utils/reports/utilizationReport';

/**
 * Separator `buildPerDishUsage` puts between a dish and its BYO choice — em dash, spaced.
 * Must stay byte-identical to `inventoryVariance.ts`'s label template or every BYO line
 * silently loses its Edit action. NOT the same as the single space `overrideByPair` uses
 * for its internal lookup key inside that file — the two are not interchangeable.
 */
export const USAGE_LABEL_SEP = ' — ';

/** A usage line plus the cost-price analysis a user would edit to change it. */
export type UsageWithCostPrice = PerDishUsage & { costPriceId?: string };

/** Dish analyses keyed by revenue name — one entry per revenue item. Built twice below:
 *  once on the exact name, once on the normalized one as a fallback. */
type DishIndex = Map<string, { id: string; overriddenChoices: string[] }>;

/**
 * Resolve the cost-price analysis behind each per-dish usage line, so the leakage drawer can
 * offer "edit dish" on it.
 *
 * Labels come from `buildPerDishUsage`, which builds them from the same fields
 * `buildPerUnitNeeds` keys on (`utilizationReport.ts` :792-809): a plain dish carries its
 * REVENUE `menuItemName` — which can differ from the analysis name, so never match on the
 * latter — and a BYO pick joins dish and choice with `USAGE_LABEL_SEP`.
 *
 * The rule that matters, and the one that is easy to get backwards: a choice the parent dish
 * OVERRIDES resolves to the PARENT's analysis, not the choice's. `buildPerDishUsage` prefers
 * the override when one exists (`inventoryVariance.ts` :938), and the overridden quantity is
 * stored in the parent's `byoChoiceOverrides`. Sending the user to the shared choice analysis
 * instead would (a) not move the number they came to fix and (b) silently change every other
 * dish built on that choice. Un-overridden picks do resolve to the choice's own analysis.
 *
 * When two analyses share a revenue item, the Edit button follows `winningDishAnalyses` —
 * the same one the needs engine charged. Anything else would open a recipe that did not
 * produce the number on the card. (A shared CHOICE *name* still resolves to nothing: those
 * are matched by name, so there is no winner to prefer.)
 */
export function attachUsageCostPriceIds(
  usage: PerDishUsage[],
  costPriceList: CostPriceAnalysis[]
): UsageWithCostPrice[] {
  const norm = (s: string) => s.trim().toLowerCase();

  // A label IS the dish's exact `menuItemName` (`inventoryVariance.ts` :2032), so the exact
  // index decides; the normalized one is only a leniency for a label whose casing drifted.
  // Two dishes differing only in casing are both real (a partner venue exports the
  // restaurant's own dishes in its own casing) — the exact index is what tells them apart,
  // and the first of them keeps the shared normalized alias rather than clobbering it.
  const dishByName: DishIndex = new Map();
  const dishByNormName: DishIndex = new Map();
  const choiceIdByName = new Map<string, string | null>();

  winningDishAnalyses(costPriceList).forEach((a) => {
    const name = a.menuItemName ?? '';
    const entry = { id: a.id, overriddenChoices: Object.keys(a.byoChoiceOverrides ?? {}) };
    dishByName.set(name, entry);
    if (!dishByNormName.has(norm(name))) dishByNormName.set(norm(name), entry);
  });
  costPriceList.forEach((a) => {
    if (a.costPriceType === COST_PRICE_TYPE_CHOICE && a.costPriceName) {
      choiceIdByName.set(a.costPriceName, choiceIdByName.has(a.costPriceName) ? null : a.id);
    }
  });

  const findDish = (name: string) => dishByName.get(name) ?? dishByNormName.get(norm(name));

  const resolve = (label: string): string | undefined => {
    const dish = findDish(label);
    if (dish) return dish.id;
    // BYO pick. A dish name may itself contain the separator, so walk every split point and
    // keep the first one whose prefix names a real dish.
    for (let at = label.indexOf(USAGE_LABEL_SEP); at !== -1; at = label.indexOf(USAGE_LABEL_SEP, at + 1)) {
      const parent = findDish(label.slice(0, at));
      if (!parent) continue;
      const choiceName = label.slice(at + USAGE_LABEL_SEP.length);
      return parent.overriddenChoices.includes(choiceName) ? parent.id : choiceIdByName.get(choiceName) ?? undefined;
    }
    return undefined;
  };

  return usage.map((u) => ({ ...u, costPriceId: resolve(u.label) }));
}
